import { clientEnv } from "@/env/client";
import { nodeEnv, vercelEnv, zendeskEnv } from "@/env/env";

export function isServer() {
  return typeof window === "undefined";
}

export function isBrowser() {
  return !isServer();
}

export function isNodeEnvTest() {
  return clientEnv.NEXT_PUBLIC_NODE_ENV === nodeEnv.test;
}

export function isNodeEnvProd() {
  return clientEnv.NEXT_PUBLIC_NODE_ENV === nodeEnv.production;
}

export function isVercelEnvProd() {
  return clientEnv.NEXT_PUBLIC_VERCEL_ENV === vercelEnv.production;
}

export function isVercelEnvLocal() {
  return clientEnv.NEXT_PUBLIC_VERCEL_ENV === vercelEnv.local;
}

export function isVercelEnvTest() {
  return clientEnv.NEXT_PUBLIC_VERCEL_ENV === vercelEnv.test;
}

// isShussanteatekinEnabled でのみ使う。ファイル外で必要になったら export する。
function isVercelEnvPreview() {
  return clientEnv.NEXT_PUBLIC_VERCEL_ENV === vercelEnv.preview;
}

export function isZendeskEnvProd() {
  return clientEnv.NEXT_PUBLIC_ZENDESK_ENV === zendeskEnv.production;
}

export const AVAILABLE_SUBDOMAIN = {
  members: "members",
  employers: "employers",
  admin: "admin",
} as const;
export const AVAILABLE_SUBDOMAINS = Object.values(AVAILABLE_SUBDOMAIN);
export type AvailableSubdomain =
  (typeof AVAILABLE_SUBDOMAIN)[keyof typeof AVAILABLE_SUBDOMAIN];

/**
 * アプリのドメインを取得する
 *   - vercelのproduction環境は NEXT_PUBLIC_APP_DOMAIN=mykenpo.com
 *   - vercelのpreview環境（developブランチ）は NEXT_PUBLIC_APP_DOMAIN=dev.mykenpo.com
 *   - vercelのpreview環境（その他のブランチ）は NEXT_PUBLIC_APP_DOMAIN=NULL で NEXT_PUBLIC_VERCEL_URL を利用する
 *   - local環境は NEXT_PUBLIC_APP_DOMAIN=local.mykenpo.com:3000
 */
export function appDomain() {
  const domain =
    clientEnv.NEXT_PUBLIC_APP_DOMAIN || clientEnv.NEXT_PUBLIC_VERCEL_URL;
  if (!domain) {
    throw new Error("appDomain is null");
  }
  return domain;
}

/**
 * pagesPath / appUrlFormat で用いる URL スキーム（ローカル HTTP 開発時のみ http）
 */
export function appUrlProtocol(): "http:" | "https:" {
  if (isVercelEnvLocal() && clientEnv.NEXT_PUBLIC_LOCAL_USE_HTTP === "true") {
    return "http:";
  }
  return "https:";
}

/**
 * パスベースルーティングかどうかを判定する
 */
export function isPathBasedRouting() {
  return clientEnv.NEXT_PUBLIC_PATH_BASED_ROUTING === "true";
}

export function appUrlFormat(subdomain: AvailableSubdomain, path: string = "") {
  const protocol = appUrlProtocol();
  if (isPathBasedRouting()) {
    // パスベース: {protocol}//{VERCEL_URL}/{subdomain}{path}
    const vercelUrl = clientEnv.NEXT_PUBLIC_VERCEL_URL;
    if (!vercelUrl) {
      throw new Error(
        "パスベースルーティングには NEXT_PUBLIC_VERCEL_URL が必要です",
      );
    }
    return `${protocol}//${vercelUrl}/${subdomain}${path}`;
  }
  // サブドメインベース: {protocol}//{subdomain}.{domain}{path}
  return `${protocol}//${subdomain}.${appDomain()}${path}`;
}

export function isDebugFlagShowMedicalCheckupInterviewQuestionIdEnable() {
  return (
    clientEnv.NEXT_PUBLIC_DEBUG_FLAG_SHOW_MEDICAL_CHECKUP_INTERVIEW_QUESTION_ID_ENV ===
    "enable"
  );
}

export function isDebugFlagShowMedicalCheckupResultInfoEnable() {
  return (
    clientEnv.NEXT_PUBLIC_DEBUG_FLAG_SHOW_MEDICAL_CHECKUP_RESULT_INFO_ENV ===
    "enable"
  );
}

export function isDebugFlagShowFacilityFeeSimulationDebugEnable() {
  return (
    clientEnv.NEXT_PUBLIC_DEBUG_FLAG_SHOW_FACILITY_FEE_SIMULATION_DEBUG_ENV ===
    "enable"
  );
}

// 対象者一覧に受検用URLのコピーを出すかのデバッグ用フラグ。
// 受検用URLに含まれる access_token は、知っていれば本人として受検できる値のため、
// 通常は画面に渡さず対象者へメールでのみ送る（受検依頼メール）。
// 動作確認でメールを経由せず受検したいときだけ有効にする。
// URL 自体は有効にしても表示せず、コピーのみできる。
export function isDebugFlagCopyStressCheckAnswerUrlEnable() {
  return (
    clientEnv.NEXT_PUBLIC_DEBUG_FLAG_COPY_STRESS_CHECK_ANSWER_URL_ENV ===
    "enable"
  );
}

export function isFeatureFlagBenefitNotificationEnable() {
  return (
    clientEnv.NEXT_PUBLIC_FEATURE_FLAG_BENEFIT_NOTIFICATION_ENV === "enable"
  );
}

// ストレスチェック機能の有効化フラグ
export function isFeatureFlagStressCheckEnabled() {
  return clientEnv.NEXT_PUBLIC_FEATURE_FLAG_STRESS_CHECK === "enable";
}
// 出産手当金 電子申請を表示する環境。
// 主要な画面が形になったため、dev 環境（develop ブランチの preview デプロイ）での
// 動作確認まで範囲を広げる。preview は Basic 認証配下のため事業所の目には触れない。
// 本番はフローが揃うまで無効のままとする。
// 環境変数のフラグにせず環境の決め打ちにしているのは、いずれ本番で有効化して
// この関数ごと消す前提の短命な分岐であり、どの環境で出すかの判断もコード側にあるため。
export function isShussanteatekinEnabled() {
  return isVercelEnvLocal() || isVercelEnvPreview();
}

// 傷病手当金の未完成の画面は dev で確認し、本番には公開しない。
// 本番で環境変数を誤って enable にしても表示されないよう、コードでも無効にする。
// 本番公開時にはこの判定を見直す。
export function isFeatureFlagShobyoteatekinEnabled() {
  if (isVercelEnvProd()) {
    return false;
  }

  return clientEnv.NEXT_PUBLIC_FEATURE_FLAG_SHOBYOTEATEKIN === "enable";
}

// 加入者向けの健診結果（検査値）画面の有効化フラグ（環境単位）
// 見られる事業所の判定はサーバー専用の環境変数を読むため、ここではなく
// src/server/models/medical-checkup/medical-checkup-results-availability.ts で行う
// （このファイルは "use client" のファイルからも読まれる）
export function isFeatureFlagMedicalCheckupResultsEnabled() {
  return (
    clientEnv.NEXT_PUBLIC_FEATURE_FLAG_MEDICAL_CHECKUP_RESULTS === "enable"
  );
}

export function isFeatureFlagHennyuujikenninEnabled() {
  return clientEnv.NEXT_PUBLIC_FEATURE_FLAG_HENNYUUJIKENNIN === "enable";
}

export function isFeatureFlagRyoyohitatekaeReceiptExtractionEnabled() {
  return (
    clientEnv.NEXT_PUBLIC_FEATURE_FLAG_RYOYOHITATEKAE_RECEIPT_EXTRACTION ===
    "enable"
  );
}
